import { getL1CategoryIdBySeoName, getL2NameById, L1CategorySeoNames } from './category-mapping';
import type { RecommendedAdWithPriceObject } from '../types/home';
import type { SrpListingTrackData } from '../types/srpListing';
import { SellerAd } from '../model/seller-other-ads-model';
import { SearchAd } from '../model/search-ads-model';
import { getPromotions } from './ga4-bff';
import {
    BaseAttribute,
    CommonAttribute,
    MotorsAttribute,
    SpecialMotorsAttributeGroup,
} from '../types/attributes';

export const checkIsSupportShipping = (
    attributes?:
        | {
              attributeGroups: SpecialMotorsAttributeGroup[];
              commonAttributes: CommonAttribute[];
          }
        | MotorsAttribute[]
        | BaseAttribute[]
): boolean => {
    return (
        Array.isArray(attributes) &&
        attributes.find((item: any) => item.key === 'support_shipping')?.value === 'True'
    );
};

export const getListingTrackData = (srpListingTrackData?: SrpListingTrackData) => {
    if (!srpListingTrackData) return {};

    return {
        category: srpListingTrackData.listingCategory ?? undefined,
        categoryId: srpListingTrackData.listingCategoryId ?? undefined,
        subcategory1: srpListingTrackData.listingSubcategory1 ?? undefined,
        subcategory2: srpListingTrackData.listingSubcategory2 ?? undefined,
        subcategory3: srpListingTrackData.listingSubcategory3 ?? undefined,
        subcategory4: srpListingTrackData.listingSubcategory4 ?? undefined,
        subcategory5: srpListingTrackData.listingSubcategory5 ?? undefined,
    };
};

/** Unlike other cases, we assume this constructor of "listing details" does not throw. */
export const getClickListingDetails = (ad: SellerAd | SearchAd): GA4.GA4ListingDetails | {} => {
    if (!ad) {
        return {};
    }

    const priceInPounds = Number(ad.priceInPounds);
    const hasPropertyPriceInPounds =
        ad.l1CategoryId === L1CategorySeoNames['flats-houses'] && Number.isFinite(priceInPounds);
    const pricePennies = hasPropertyPriceInPounds
        ? priceInPounds * 100
        : ad.price
        ? Number(ad.price.replace('£', ''))
        : undefined;
    const price = hasPropertyPriceInPounds
        ? priceInPounds
        : typeof pricePennies === 'number'
        ? pricePennies / 100
        : undefined;
    const promotions = getPromotions(ad);
    const postedDate = ad.postedDate?.replace(' ago', '');
    const isTrade = ad.attributes?.find((attr) => attr.key === 'seller_type')?.value === 'Trade';

    return {
        id: ad.id,
        age: postedDate,
        contactEmail: undefined,
        contactNumber: undefined,
        type: undefined,
        cost: undefined,
        totalCost: undefined,
        isTrade: isTrade,
        location: ad.location,
        locationId: undefined,
        promotions: promotions?.join(','),
        locationHierarchy: undefined,
        locationShowOnMap: undefined,
        name: ad.title,
        numberOfImages: ad.numberOfImages,
        numberWordsDescription: ad.shortDescription?.split(/\s+/).length ?? 0,
        price,
        pricePennies,
        category: getL2NameById(ad.l2CategoryId) ?? ad.categoryId,
        categoryId: ad.l2CategoryId ?? ad.l1CategoryId,
        subcategory1: ad.l1CategoryId ?? undefined,
        subcategory2: ad.l2CategoryId ?? undefined,
        subcategory3: ad?.srpListingTrackData?.listingSubcategory3 ?? undefined,
        subcategory4: ad?.srpListingTrackData?.listingSubcategory4 ?? undefined,
        subcategory5: ad?.srpListingTrackData?.listingSubcategory5 ?? undefined,
        isGBGVerified: Boolean(ad.identityVerificationModel?.status),
        videoLink: ad.hasVideo,
        websiteLink: undefined,
        ...(ad?.extendMap || {}),
    };
};

/** Unlike other cases, we assume this constructor of "listing details" does not throw. */
export const getUserFeedClickListingDetails = (
    ad: RecommendedAdWithPriceObject
): GA4.GA4ListingDetails | {} => {
    if (!ad) {
        return {};
    }

    const pricePennies = ad.price.type === 'FREE' ? undefined : ad.price.amount;
    const price = typeof pricePennies === 'number' ? pricePennies / 100 : undefined;
    const promotions = [`spotlight=${ad.spotlight}`];
    const postedDate = ad?.timeSincePosted?.replace(' ago', '');

    return {
        id: ad.id,
        age: postedDate,
        contactEmail: undefined,
        contactNumber: undefined,
        type: undefined,
        cost: undefined,
        totalCost: undefined,
        isTrade: undefined,
        location: ad.location,
        locationId: undefined,
        promotions,
        locationHierarchy: undefined,
        locationShowOnMap: undefined,
        name: ad.title,
        numberOfImages: ad.numberOfImages ?? undefined,
        numberWordsDescription: ad.shortDescription?.split(/\s+/).length ?? undefined,
        price,
        pricePennies,
        category: ad.category,
        categoryId: getL1CategoryIdBySeoName(ad.categoryId ?? (ad.l1Category as any)),
        subcategory1: ad.l1CategoryId ?? ad.l1Category ?? undefined,
        subcategory2: ad.l2CategoryId ?? undefined,
        subcategory3: ad?.srpListingTrackData?.listingSubcategory3 ?? undefined,
        subcategory4: ad?.srpListingTrackData?.listingSubcategory4 ?? undefined,
        subcategory5: ad?.srpListingTrackData?.listingSubcategory5 ?? undefined,
        websiteLink: undefined,
    };
};

export const getClickListingDetailsV2 = (ad: SellerAd | SearchAd): GA4.GA4ListingDetails | {} => {
    if (!ad) {
        return {};
    }

    const listingDetails = getClickListingDetails(ad);
    const listingCategory = getListingTrackData(ad?.srpListingTrackData);

    return {
        ...listingDetails,
        ...listingCategory,
    };
};

export const getUserFeedClickListingDetailsV2 = (
    ad: RecommendedAdWithPriceObject
): GA4.GA4ListingDetails | {} => {
    if (!ad) {
        return {};
    }

    const listingDetails = getUserFeedClickListingDetails(ad);
    const listingCategory = getListingTrackData(ad?.srpListingTrackData);

    return {
        ...listingDetails,
        ...listingCategory,
    };
};
